import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  HiOutlineUsers, 
  HiOutlinePencil, 
  HiOutlineTrash, 
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineLocationMarker,
  HiOutlineBriefcase,
  HiOutlinePlus,
  HiOutlineEye,
  HiOutlineArrowLeft,
  HiOutlineLink,
  HiOutlineChat,
  // HiOutlineBell,
  HiOutlineFlag,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineExclamation,
  HiOutlineCalendar
} from 'react-icons/hi';
import { Contact, Application, Company, UpdateContactData, CreateInteractionData } from '../types';
import { contactsService } from '../services/contactsService';
import { applicationsService } from '../services/applicationsService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { useEscapeModal, hasFormChanges } from '../utils/useEscapeModal';

const ContactDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [contact, setContact] = useState<Contact | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  // const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  
  const [editForm, setEditForm] = useState<UpdateContactData>({
    name: '',
    email: '',
    phone: '',
    title: '',
    department: '',
    linkedin: '',
    twitter: '',
    relationship: 'other',
    howMet: 'other',
    notes: '',
    tags: [],
    priority: 'medium',
    nextFollowUp: '',
    followUpNotes: ''
  });

  const [interactionForm, setInteractionForm] = useState<CreateInteractionData>({
    type: 'email',
    date: new Date().toISOString().split('T')[0],
    subject: '',
    notes: '',
    outcome: 'neutral'
  });

  // Initial interaction form data for change detection
  const [initialInteractionForm, setInitialInteractionForm] = useState<CreateInteractionData>({
    type: 'email',
    date: new Date().toISOString().split('T')[0],
    subject: '',
    notes: '',
    outcome: 'neutral'
  });

  // const [followUpForm, setFollowUpForm] = useState({
  //   date: '',
  //   notes: ''
  // });

  const [newTag, setNewTag] = useState('');

  const relationshipOptions = [
    { value: 'colleague', label: 'Colleague' },
    { value: 'manager', label: 'Manager' },
    { value: 'hr', label: 'HR' },
    { value: 'recruiter', label: 'Recruiter' },
    { value: 'friend', label: 'Friend' },
    { value: 'referral', label: 'Referral' },
    { value: 'other', label: 'Other' }
  ];

  const howMetOptions = [
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'conference', label: 'Conference' },
    { value: 'referral', label: 'Referral' },
    { value: 'networking_event', label: 'Networking Event' },
    { value: 'online', label: 'Online' },
    { value: 'previous_job', label: 'Previous Job' },
    { value: 'other', label: 'Other' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const interactionTypes = [
    { value: 'email', label: 'Email', icon: HiOutlineMail },
    { value: 'phone', label: 'Phone', icon: HiOutlinePhone },
    { value: 'linkedin', label: 'LinkedIn', icon: HiOutlineLink },
    { value: 'meeting', label: 'Meeting', icon: HiOutlineUsers },
    { value: 'interview', label: 'Interview', icon: HiOutlineBriefcase },
    { value: 'referral', label: 'Referral', icon: HiOutlineFlag },
    { value: 'other', label: 'Other', icon: HiOutlineChat }
  ];

  const fetchContactData = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const [contactRes, applicationsRes] = await Promise.all([
        contactsService.getContact(id),
        applicationsService.getApplicationsByReferral(id, { limit: 100 })
      ]);

      console.log("Applications", applicationsRes.data, id);

      setContact(contactRes);
      setApplications(applicationsRes.data);
      
      // Initialize edit form
      setEditForm({
        name: contactRes.name || '',
        email: contactRes.email || '',
        phone: contactRes.phone || '',
        title: contactRes.title || '',
        department: contactRes.department || '',
        linkedin: contactRes.linkedin || '',
        twitter: contactRes.twitter || '',
        relationship: contactRes.relationship || 'other',
        howMet: contactRes.howMet || 'other',
        notes: contactRes.notes || '',
        tags: contactRes.tags || [],
        priority: contactRes.priority || 'medium',
        nextFollowUp: contactRes.nextFollowUp ? new Date(contactRes.nextFollowUp).toISOString().split('T')[0] : '',
        followUpNotes: contactRes.followUpNotes || ''
      });
    } catch (error) {
      console.error('Error fetching contact data:', error);
      toast.error('Failed to load contact data');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id) {
      fetchContactData();
    }
  }, [id, fetchContactData]);

  const handleEdit = async () => {
    if (!id || !contact) return;

    try {
      const updatedContact = await contactsService.updateContact(id, editForm);
      setContact(updatedContact);
      setEditMode(false);
      toast.success('Contact updated successfully');
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update contact');
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      await contactsService.deleteContact(id);
      toast.success('Contact deleted successfully');
      navigate('/contacts');
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  const handleAddInteraction = async () => {
    if (!id) return;

    try {
      const updatedContact = await contactsService.addInteraction(id, interactionForm);
      setContact(updatedContact);
      resetInteractionForm();
      toast.success('Interaction added successfully');
    } catch (error) {
      console.error('Error adding interaction:', error);
      toast.error('Failed to add interaction');
    }
  };

  const resetInteractionForm = () => {
    const defaultFormData = {
      type: 'email' as const,
      date: new Date().toISOString().split('T')[0],
      subject: '',
      notes: '',
      outcome: 'neutral' as const
    };

    setInteractionForm(defaultFormData);
    setInitialInteractionForm(defaultFormData);
    setShowInteractionModal(false);
  };

  // Check if interaction form has unsaved changes
  const hasUnsavedInteractionChanges = hasFormChanges(interactionForm, initialInteractionForm);

  // Handle ESC key to close interaction modal with confirmation for unsaved changes
  useEscapeModal({
    isOpen: showInteractionModal,
    onClose: resetInteractionForm,
    hasUnsavedChanges: hasUnsavedInteractionChanges,
    confirmMessage: 'You have unsaved changes. Are you sure you want to close without saving?'
  });

  // const handleScheduleFollowUp = async () => {
  //   if (!id) return;

  //   try {
  //     await contactsService.updateContactFollowUp(id, followUpForm.date, followUpForm.notes);
  //     await fetchContactData(); // Refresh data
  //     setShowFollowUpModal(false);
  //     setFollowUpForm({ date: '', notes: '' });
  //     toast.success('Follow-up scheduled successfully');
  //   } catch (error) {
  //     console.error('Error scheduling follow-up:', error);
  //     toast.error('Failed to schedule follow-up');
  //   }
  // };

  const addTag = () => {
    if (newTag.trim() && !editForm.tags?.includes(newTag.trim())) {
      setEditForm({
        ...editForm,
        tags: [...(editForm.tags || []), newTag.trim()]
      });
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setEditForm({
      ...editForm,
      tags: editForm.tags?.filter(tag => tag !== tagToRemove) || []
    });
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };
    return colors[priority as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getOutcomeColor = (outcome: string) => {
    const colors = {
      positive: 'bg-green-100 text-green-800',
      negative: 'bg-red-100 text-red-800',
      neutral: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return colors[outcome as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getOutcomeIcon = (outcome: string) => {
    switch (outcome) {
      case 'positive': return <HiOutlineCheck className="h-4 w-4" />;
      case 'negative': return <HiOutlineX className="h-4 w-4" />;
      case 'pending': return <HiOutlineExclamation className="h-4 w-4" />;
      default: return null;
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    
    const getOrdinalSuffix = (day: number): string => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getCompanyName = (company: string | Company): string => {
    return typeof company === 'string' ? 'Unknown Company' : company?.name || 'Unknown Company';
  };

  const getStatusColor = (status: string) => {
    const colors = {
      applied: 'bg-blue-100 text-blue-800',
      under_review: 'bg-purple-100 text-purple-800',
      phone_screening: 'bg-yellow-100 text-yellow-800',
      technical_test: 'bg-orange-100 text-orange-800',
      interview_scheduled: 'bg-indigo-100 text-indigo-800',
      interviewed: 'bg-cyan-100 text-cyan-800',
      final_round: 'bg-teal-100 text-teal-800',
      offer_received: 'bg-green-100 text-green-800',
      offer_accepted: 'bg-green-200 text-green-900',
      offer_declined: 'bg-gray-100 text-gray-800',
      rejected: 'bg-red-100 text-red-800',
      withdrawn: 'bg-gray-100 text-gray-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      applied: 'Applied',
      under_review: 'Under Review',
      phone_screening: 'Phone Screening',
      technical_test: 'Technical Test',
      interview_scheduled: 'Interview Scheduled',
      interviewed: 'Interviewed',
      final_round: 'Final Round',
      offer_received: 'Offer Received',
      offer_accepted: 'Offer Accepted',
      offer_declined: 'Offer Declined',
      rejected: 'Rejected',
      withdrawn: 'Withdrawn'
    };
    return labels[status as keyof typeof labels] || status;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="text-center py-12">
        <HiOutlineUsers className="mx-auto h-12 w-12 text-secondary-400" />
        <h3 className="mt-2 text-sm font-medium text-secondary-900">Contact not found</h3>
        <p className="mt-1 text-sm text-secondary-500">
          The contact you're looking for doesn't exist or has been removed.
        </p>
        <button
          onClick={() => navigate('/contacts')}
          className="mt-4 btn btn-primary"
        >
          Back to Contacts
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/contacts')}
            className="p-2 text-secondary-400 hover:text-secondary-600 rounded-full hover:bg-secondary-100"
          >
            <HiOutlineArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">{contact.name}</h1>
            <p className="mt-1 text-sm text-secondary-600">
              Contact Details and Interaction History
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          {!editMode ? (
            <>
              <button
                onClick={() => {
                  resetInteractionForm();
                  setShowInteractionModal(true);
                }}
                className="btn btn-secondary"
              >
                <HiOutlinePlus className="h-4 w-4 mr-2" />
                Add Interaction
              </button>
              {/* <button
                onClick={() => setShowFollowUpModal(true)}
                className="btn btn-secondary"
              >
                <HiOutlineBell className="h-4 w-4 mr-2" />
                Schedule Follow-up
              </button> */}
              <button
                onClick={() => setEditMode(true)}
                className="btn btn-secondary"
              >
                <HiOutlinePencil className="h-4 w-4 mr-2" />
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="btn bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
              >
                <HiOutlineTrash className="h-4 w-4 mr-2" />
                Delete
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleEdit}
                className="btn btn-primary px-4 py-2"
              >
                Save Changes
              </button>
              <button
                onClick={() => {
                  setEditMode(false);
                  // Reset form to original values
                  setEditForm({
                    name: contact.name || '',
                    email: contact.email || '',
                    phone: contact.phone || '',
                    title: contact.title || '',
                    department: contact.department || '',
                    linkedin: contact.linkedin || '',
                    twitter: contact.twitter || '',
                    relationship: contact.relationship || 'other',
                    howMet: contact.howMet || 'other',
                    notes: contact.notes || '',
                    tags: contact.tags || [],
                    priority: contact.priority || 'medium',
                    nextFollowUp: contact.nextFollowUp ? new Date(contact.nextFollowUp).toISOString().split('T')[0] : '',
                    followUpNotes: contact.followUpNotes || ''
                  });
                }}
                className="btn btn-secondary px-4 py-2 bg-secondary-100"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Contact Information & Company */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Contact Info */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-secondary-900">Contact Information</h3>
            </div>
            <div className="card-content space-y-6">
              {editMode ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="input w-full"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Job Title
                    </label>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      className="input w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      className="input w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      className="input w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Department
                    </label>
                    <input
                      type="text"
                      value={editForm.department}
                      onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                      className="input w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={editForm.priority}
                      onChange={(e) => setEditForm({ ...editForm, priority: e.target.value as any })}
                      className="input w-full"
                    >
                      {priorityOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      LinkedIn
                    </label>
                    <input
                      type="url"
                      value={editForm.linkedin}
                      onChange={(e) => setEditForm({ ...editForm, linkedin: e.target.value })}
                      className="input w-full"
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Twitter
                    </label>
                    <input
                      type="url"
                      value={editForm.twitter}
                      onChange={(e) => setEditForm({ ...editForm, twitter: e.target.value })}
                      className="input w-full"
                      placeholder="https://twitter.com/..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Relationship
                    </label>
                    <select
                      value={editForm.relationship}
                      onChange={(e) => setEditForm({ ...editForm, relationship: e.target.value as any })}
                      className="input w-full"
                    >
                      {relationshipOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      How We Met
                    </label>
                    <select
                      value={editForm.howMet}
                      onChange={(e) => setEditForm({ ...editForm, howMet: e.target.value as any })}
                      className="input w-full"
                    >
                      {howMetOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      rows={3}
                      className="input w-full"
                      placeholder="Personal notes about this contact..."
                    />
                  </div>

                  {/* Tags Management */}
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Tags
                    </label>
                    <div className="flex space-x-2 mb-2">
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addTag()}
                        className="input flex-1"
                        placeholder="Add a tag..."
                      />
                      <button
                        type="button"
                        onClick={addTag}
                        className="btn btn-secondary"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {editForm.tags?.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="ml-1 h-4 w-4 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-secondary-500">Job Title</h4>
                      <p className="mt-1 text-sm text-secondary-900">
                        {contact.title || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-secondary-500">Department</h4>
                      <p className="mt-1 text-sm text-secondary-900">
                        {contact.department || 'Not specified'}
                      </p>
                    </div>
                  </div>

                  {/* Contact Methods */}
                  <div className="space-y-3">
                    {contact.email && (
                      <div className="flex items-center text-sm text-secondary-600">
                        <HiOutlineMail className="h-4 w-4 mr-2" />
                        <a href={`mailto:${contact.email}`} className="text-blue-600 hover:text-blue-800">
                          {contact.email}
                        </a>
                      </div>
                    )}
                    {contact.phone && (
                      <div className="flex items-center text-sm text-secondary-600">
                        <HiOutlinePhone className="h-4 w-4 mr-2" />
                        <a href={`tel:${contact.phone}`} className="text-blue-600 hover:text-blue-800">
                          {contact.phone}
                        </a>
                      </div>
                    )}
                    {contact.linkedin && (
                      <div className="flex items-center text-sm text-secondary-600">
                        <HiOutlineLink className="h-4 w-4 mr-2" />
                        <a
                          href={contact.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                        >
                          LinkedIn Profile
                        </a>
                      </div>
                    )}
                    {contact.twitter && (
                      <div className="flex items-center text-sm text-secondary-600">
                        <HiOutlineLink className="h-4 w-4 mr-2" />
                        <a
                          href={contact.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Twitter Profile
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Relationship Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-secondary-500">Relationship</h4>
                      <p className="mt-1 text-sm text-secondary-900 capitalize">
                        {contact.relationship}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-secondary-500">How We Met</h4>
                      <p className="mt-1 text-sm text-secondary-900 capitalize">
                        {contact.howMet.replace('_', ' ')}
                      </p>
                    </div>
                  </div>

                  {/* Tags */}
                  {contact.tags && contact.tags.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-secondary-500 mb-2">Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {contact.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {contact.notes && (
                    <div>
                      <h4 className="text-sm font-medium text-secondary-500">Notes</h4>
                      <p className="mt-1 text-sm text-secondary-900 whitespace-pre-wrap">
                        {contact.notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - Company & Stats */}
        <div className="space-y-6">
          {/* Company Card */}
          {contact.company && typeof contact.company === 'object' && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-secondary-900">Company</h3>
              </div>
              <div className="card-content">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-secondary-900">{contact.company.name}</h4>
                    {contact.company.location && (
                      <p className="text-sm text-secondary-500 mt-1">
                        <HiOutlineLocationMarker className="inline h-4 w-4 mr-1" />
                        {contact.company.location}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => navigate(`/companies/${(contact.company as Company)._id}`)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <HiOutlineEye className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Priority & Follow-up */}
          <div className="card">
            <div className="card-content space-y-4 mt-4">
              <div>
                <h4 className="text-sm font-medium text-secondary-500">Priority</h4>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(contact.priority)} mt-1`}>
                  {contact.priority.charAt(0).toUpperCase() + contact.priority.slice(1)}
                </span>
              </div>

              {contact.nextFollowUp && (
                <div>
                  <h4 className="text-sm font-medium text-secondary-500">Next Follow-up</h4>
                  <p className="mt-1 text-sm text-secondary-900">
                    {formatDate(contact.nextFollowUp)}
                  </p>
                  {contact.followUpNotes && (
                    <p className="mt-1 text-xs text-secondary-600">
                      {contact.followUpNotes}
                    </p>
                  )}
                </div>
              )}

              <div>
                <h4 className="text-sm font-medium text-secondary-500">Last Interaction</h4>
                <p className="mt-1 text-sm text-secondary-900">
                  {formatDate(contact.lastInteraction)}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-secondary-500">Total Interactions</h4>
                <p className="mt-1 text-sm text-secondary-900">
                  {contact.interactions?.length || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interaction History */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-medium text-secondary-900">Interaction History</h3>
          <button
            onClick={() => {
              resetInteractionForm();
              setShowInteractionModal(true);
            }}
            className="btn btn-sm btn-primary"
          >
            <HiOutlinePlus className="h-4 w-4 mr-1" />
            Add Interaction
          </button>
        </div>
        <div className="card-content">
          {contact.interactions && contact.interactions.length > 0 ? (
            <div className="space-y-4">
              {contact.interactions
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                .map((interaction, index) => {
                  const InteractionIcon = interactionTypes.find(t => t.value === interaction.type)?.icon || HiOutlineChat;
                  return (
                    <div
                      key={index}
                      className="flex items-start space-x-3 py-3 border-b border-secondary-200 last:border-b-0"
                    >
                      <div className="flex-shrink-0">
                        <InteractionIcon className="h-5 w-5 text-secondary-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-secondary-900 capitalize">
                              {interaction.type}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getOutcomeColor(interaction.outcome)}`}>
                              {getOutcomeIcon(interaction.outcome)}
                              <span className="ml-1 capitalize">{interaction.outcome}</span>
                            </span>
                          </div>
                          <span className="text-xs text-secondary-500">
                            {formatDateTime(interaction.date)}
                          </span>
                        </div>
                        {interaction.subject && (
                          <h4 className="text-sm text-secondary-900 mt-1">
                            {interaction.subject}
                          </h4>
                        )}
                        {interaction.notes && (
                          <p className="text-sm text-secondary-600 mt-1">
                            {interaction.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="text-center py-6">
              <HiOutlineChat className="mx-auto h-8 w-8 text-secondary-400" />
              <p className="mt-2 text-sm text-secondary-500">No interactions yet</p>
              <button
                onClick={() => {
                  resetInteractionForm();
                  setShowInteractionModal(true);
                }}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                Add the first interaction
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Related Applications */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-medium text-secondary-900">Applications (Referrals)</h3>
          <button
            onClick={() => navigate('/applications')}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            View all applications
          </button>
        </div>
        <div className="card-content">
          {applications.length > 0 ? (
            <div className="space-y-4">
              {applications.map((application) => (
                <div
                  key={application._id}
                  onClick={() => navigate(`/applications/${application._id}`)}
                  className="bg-white rounded-lg border border-secondary-200 hover:border-primary-300 hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden"
                >
                  {/* Header */}
                  <div className="bg-gradient-to-r from-primary-50 to-secondary-50 px-4 py-3 border-b border-secondary-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-secondary-900 truncate">
                            {application.role || 'No Role Specified'}
                          </h4>
                          <p className="text-xs text-secondary-600 mt-0.5">
                            at {getCompanyName(application.company)}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                            {getStatusLabel(application.status)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-1.5 text-xs text-secondary-600">
                          <HiOutlineCalendar className="h-3.5 w-3.5 text-primary-500" />
                          <span>Applied {formatDate(application.appliedDate)}</span>
                        </div>
                      </div>
                      
                      <div className="ml-4">
                        <HiOutlineEye className="h-4 w-4 text-secondary-400" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="px-4 py-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Left side - Application details */}
                      <div className="space-y-2">
                        {application.priority && application.priority !== 'medium' && (
                          <div className="flex items-center gap-2 text-xs text-secondary-600">
                            <HiOutlineFlag className="h-3.5 w-3.5 text-primary-500" />
                            <span className="capitalize font-medium">{application.priority} Priority</span>
                          </div>
                        )}
                        
                        {application.source && application.source !== 'other' && (
                          <div className="flex items-center gap-2 text-xs text-secondary-600">
                            <HiOutlineBriefcase className="h-3.5 w-3.5 text-primary-500" />
                            <span>Source: {application.source.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                          </div>
                        )}
                        
                        {application.salary && (application.salary.offered || application.salary.negotiated) && (
                          <div className="flex items-center gap-2 text-xs text-secondary-600">
                            <span className="text-primary-500 font-medium">💰</span>
                            <span>
                              {application.salary.offered && `${application.salary.offered.toLocaleString()} ${application.salary.currency}`}
                              {application.salary.negotiated && application.salary.offered && ' → '}
                              {application.salary.negotiated && !application.salary.offered && `${application.salary.negotiated.toLocaleString()} ${application.salary.currency}`}
                              {application.salary.negotiated && application.salary.offered && `${application.salary.negotiated.toLocaleString()} ${application.salary.currency}`}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Right side - Follow-up and notes */}
                      <div className="space-y-2">
                        {application.followUpDate && (
                          <div className={`p-2 rounded-md text-xs ${new Date(application.followUpDate) > new Date() ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-600'}`}>
                            <div className="flex items-center gap-1.5">
                              <HiOutlineExclamation className="h-3.5 w-3.5" />
                              <span className="font-medium">
                                {new Date(application.followUpDate) > new Date() ? 'Follow up: ' : 'Followed up: '}
                                {formatDate(application.followUpDate)}
                              </span>
                            </div>
                            {application.followUpNotes && (
                              <p className="mt-1 text-xs opacity-90 line-clamp-1">
                                {application.followUpNotes}
                              </p>
                            )}
                          </div>
                        )}
                        
                        {application.notes && (
                          <div className="text-xs text-secondary-600">
                            <p className="line-clamp-2 leading-relaxed">
                              {application.notes}
                            </p>
                          </div>
                        )}
                        
                        {(application.status === 'offer_received' || application.status === 'offer_accepted') && (
                          <div className="p-2 bg-green-50 text-green-700 rounded-md text-xs">
                            <div className="flex items-center gap-1.5">
                              <span>🎉</span>
                              <span className="font-medium">
                                {application.status === 'offer_received' ? 'Offer Received!' : 'Offer Accepted!'}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {application.status === 'rejected' && (
                          <div className="p-2 bg-red-50 text-red-700 rounded-md text-xs">
                            <div className="flex items-center gap-1.5">
                              <span>❌</span>
                              <span className="font-medium">Application Rejected</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <HiOutlineBriefcase className="mx-auto h-8 w-8 text-secondary-400" />
              <p className="mt-2 text-sm text-secondary-500">No applications with this referral</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Interaction Modal */}
      {showInteractionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-medium text-secondary-900 mb-4">
              Add Interaction
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Type
                </label>
                <select
                  value={interactionForm.type}
                  onChange={(e) => setInteractionForm({ ...interactionForm, type: e.target.value as any })}
                  className="input w-full"
                >
                  {interactionTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={interactionForm.date}
                  onChange={(e) => setInteractionForm({ ...interactionForm, date: e.target.value })}
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Subject (Optional)
                </label>
                <input
                  type="text"
                  value={interactionForm.subject}
                  onChange={(e) => setInteractionForm({ ...interactionForm, subject: e.target.value })}
                  className="input w-full"
                  placeholder="Brief subject or title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Outcome
                </label>
                <select
                  value={interactionForm.outcome}
                  onChange={(e) => setInteractionForm({ ...interactionForm, outcome: e.target.value as any })}
                  className="input w-full"
                >
                  <option value="positive">Positive</option>
                  <option value="negative">Negative</option>
                  <option value="neutral">Neutral</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={interactionForm.notes}
                  onChange={(e) => setInteractionForm({ ...interactionForm, notes: e.target.value })}
                  rows={3}
                  className="input w-full"
                  placeholder="Details about the interaction..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowInteractionModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleAddInteraction}
                className="btn btn-primary"
              >
                Add Interaction
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Follow-up Modal */}
      {/* {showFollowUpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-medium text-secondary-900 mb-4">
              Schedule Follow-up
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Follow-up Date
                </label>
                <input
                  type="date"
                  value={followUpForm.date}
                  onChange={(e) => setFollowUpForm({ ...followUpForm, date: e.target.value })}
                  className="input w-full"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Follow-up Notes
                </label>
                <textarea
                  value={followUpForm.notes}
                  onChange={(e) => setFollowUpForm({ ...followUpForm, notes: e.target.value })}
                  rows={3}
                  className="input w-full"
                  placeholder="Reminder notes for the follow-up..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowFollowUpModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleFollowUp}
                className="btn btn-primary"
              >
                Schedule Follow-up
              </button>
            </div>
          </div>
        </div>
      )} */}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-medium text-secondary-900 mb-4">
              Delete Contact
            </h3>
            <p className="text-sm text-secondary-600 mb-6">
              Are you sure you want to delete "{contact.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="btn bg-red-600 text-white hover:bg-red-700"
              >
                Delete Contact
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactDetail; 